// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.SubscriptionGrid.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

/**
 * ReportSubscriptionGrid component
 */
import m from 'mobx-react';
import React, { Component, PropTypes } from 'react';
import connect from 'share/enhancers/Izenda.Connect';
import classnames from 'classnames';
import OverflowText from 'share/components/controls/Izenda.OverflowText';
import DataGridSearch from 'share/components/controls/DataGrid/Izenda.DataGridSearch';
import { Table, Column } from 'share/components/controls/DataGrid/Izenda.Table';
import Button from 'share/components/controls/Izenda.Button';
import Paginator from 'share/components/controls/DataGrid/helpers/Izenda.Paginator';
import Input from 'share/components/controls/Izenda.Input';
import SubscriptionDetails from 'logic/models/Izenda.SubscriptionDetails';
import ObjectStateConstants from 'logic/data/Izenda.ObjectStateConstants';
import GridLayout from 'share/components/controls/Izenda.GridLayout';
import SubscriptionPopupDesigner from 'modules/Subscription/Izenda.SubscriptionPopupDesigner';
import EventsUtils, { notifyChange } from 'EventUtils';
import uiState from 'logic/Izenda.UiState';
import ReportSubscriptionConstants from 'logic/data/Izenda.ReportSubscriptionConstants';
import { isNil, map, split, values, getOr, merge, placeholder } from 'lodash/fp';
@connect({ toClass: true })
class SubscriptionGrid extends Component {
  static displayName = 'SubscriptionGrid';

  static propTypes = {
    isPopupMode: PropTypes.bool,
    store: PropTypes.object,
    pageConstants: PropTypes.object.isRequired,
    onSearch: PropTypes.func,
    onDelete: PropTypes.func,
    onCopy: PropTypes.func,
    onSavePopupDesigner: PropTypes.func.isRequired,
    onSyncSubscription: PropTypes.func
  };

  constructor() {
    super();

    this.state = {
      isDataSearchMinSize: uiState.isMobile
    };
  }

  /**
   * Handle when sort grid
   */
  onSortChange = () => {
    let {
      store,
      store: { model },
      confirmDirtyForm
    } = this.props;
    confirmDirtyForm(model.checkDirty()).then(function() {
      store.onSortChange();
    });
  };

  renderSelectCell(data: any, key: string, rowIndex: number, column: any) {
    const onChange = value => {
      data.updateSelectedValue(value);
    };

    return (
      <input
        type='checkbox'
        value={data[key]}
        checked={data[key]}
        onChange={notifyChange(onChange, EventsUtils.CHECKBOX)}
      />
    );
  }

  renderTextCell = (data: any, key: string, rowIndex: number, column: any) => {
    switch (key) {
      case 'recipients':
        return this.renderSeparatedLine(data[key], ';');
      case 'filterValueSelection':
        return this.renderSeparatedLine(data[key], ';#');
      default:
        return <OverflowText tooltip={data[key]} text={data[key]} />;
    }
  };

  renderAction = (data: any, key: string, rowIndex: number, column: any) => {
    let {
      store,
      pageConstants: { PAGE_TEXT: pageText, MESSAGE },
      onCopy,
      onSyncSubscription,
      doesSaveToDB,
      confirmFn,
      onDelete
    } = this.props;

    const onCopySetting = () => {
      if (!isNil(onCopy)) {
        onCopy(data.id);
      } else {
        store.copySubscribedInstance(data.id);
        onSyncSubscription && onSyncSubscription(store.model.subscribedInstances);
        doesSaveToDB && this.onClickSave();
      }
    };

    const onDeleteSetting = (e: any) => {
      confirmFn('Confirmation', {
        description: MESSAGE.M01a
      }).then(() => {
        if (!isNil(onDelete)) {
          onDelete([data.id]);
        } else {
          store.removeSubscribedInstances([data.id]);
          onSyncSubscription && onSyncSubscription(store.model.subscribedInstances);
          doesSaveToDB && this.onClickSave();
        }
      });
    };

    const onClickEdit = (e: any) => {
      data.updateDataForEditMode({
        emailTemplates: store.model.emailTemplates,
        state:
          isNil(data.id) || data.id.indexOf('newInstance_') >= 0
            ? ObjectStateConstants.INSERT
            : ObjectStateConstants.UPDATE,
        isSubscription: store.model.isSubscriptionMode
      });
      this.showSubscriptionPopup(data);
    };

    return (
      <div>
        <a>
          <span title={pageText.EditText} className='icon-pencil' onClick={onClickEdit} value={data} />
        </a>
        <a>
          <span title={pageText.CopyText} className='icon-copy' onClick={onCopySetting} value={data} />
        </a>
        <a>
          <span title={pageText.DeleteText} className='icon-cancel' onClick={onDeleteSetting} value={rowIndex} />
        </a>
      </div>
    );
  };

  getSelectedInstanceIds = () => {
    let store = this.props.store || {};
    const ids = placeholder(store.activeInstances)
      .chain()
      .filter(item => item.selectedValue)
      .map(i => i.id)
      .value();

    return ids;
  };

  onClickDelete = (e: any) => {
    let { store, confirmFn, pageConstants, onSyncSubscription, doesSaveToDB, onDelete } = this.props;

    confirmFn('Confirmation', {
      description: pageConstants.MESSAGE.M01b
    }).then(() => {
      let ids = this.getSelectedInstanceIds();
      if (!isNil(onDelete)) {
        onDelete(ids);
      } else {
        store.removeSubscribedInstances(ids);
        onSyncSubscription && onSyncSubscription(store.model.subscribedInstances);
        doesSaveToDB && this.onClickSave();
      }
    });
  };

  onClickAdd = () => {
    let { store, confirmMessage } = this.props;
    if (store.reportId && isNil(uiState.currentUser.emailAddress)) {
      return confirmMessage('error', ReportSubscriptionConstants.MESSAGE.M04);
    }
    let subscriptionDetails = new SubscriptionDetails(
      {
        dashboardId: store.dashboardId,
        reportId: store.reportId,
        state: ObjectStateConstants.INSERT,
        isSubscription: store.model.isSubscriptionMode
      },
      store.model.emailTemplates
    );

    let subData = subscriptionDetails.init();
    this.showSubscriptionPopup(subData);
  };

  /**
   * Show subscription popup.
   */
  showSubscriptionPopup = (data: any) => {
    let {
      store,
      openPopup,
      reportState,
      onSavePopupDesigner,
      dashboardDetail,
      updateCascadingFilterValue
    } = this.props;
    let model = store.model;
    return openPopup(SubscriptionPopupDesigner, {
      reportState: reportState,
      data: data,
      onSave: onSavePopupDesigner,
      onCheckDuplicateInstanceName: model.checkDuplicateInstanceName.bind(model),
      onValidateSubscription: store.validateSubscription.bind(store),
      dashboardDetail: dashboardDetail,
      store: store,
      updateCascadingFilterValue: updateCascadingFilterValue
    });
  };

  checkAll = (value: boolean) => {
    this.props.store.model.selectAll(value);
  };

  beforeClosing = (valManager: any, btnType: string) => {
    let store = this.props.store || {};
    if (btnType === 'cancel') {
      return this.props.confirmDirtyForm(store.model.checkDirty() === true).then(() => true);
    } else {
      return true;
    }
  };

  /**
   * Render recipients
   */
  renderSeparatedLine(data: any, separator: any) {
    if (data) {
      return map(item => <OverflowText tooltip={item} text={item} />, split(separator, data));
    }
  }

  onClickClose = () => {
    let props = this.props;
    let store = props.store || {};
    let isDirty = store.model.checkDirty();

    const close = () => {
      props.onClose && props.onClose();
    };

    if (isDirty) {
      props.confirmDirtyForm(isDirty).then(() => {
        close();
      });
    } else {
      close();
    }
  };

  onClickSave = () => {
    this.props.onSave && this.props.onSave();
  };

  /**
   * Render table component
   */
  renderTable = () => {
    let {
      store,
      pageConstants: { TABLE_DEF: tableDef }
    } = this.props;
    let model = store.model || {};
    let pageInfo = model.pageInfo || {};
    let data = model.activeInstances || [];

    const checkBoxSelectAllProps = {
      checked: model.hasAllActiveInstancesSelected,
      onChange: notifyChange(this.checkAll, EventsUtils.CHECKBOX),
      type: 'checkbox'
    };

    const selectRowDefinition = merge(
      tableDef['selectedValue'],
      {
        headerComponent: Input,
        headerComponentProps: checkBoxSelectAllProps
      },
      {}
    );

    /**
     * Render columns component
     */
    const renderColumns = () => {
      return map(item => {
        switch (item.key) {
          case 'selectedValue':
            return <Column {...selectRowDefinition} cell={this.renderSelectCell} />;
          case 'action':
            return <Column {...item} cell={this.renderAction} />;
          default:
            return <Column {...item} cell={this.renderTextCell} />;
        }
      }, values(tableDef));
    };

    return (
      <Table dataSource={data} onSortChange={this.onSortChange} draggableTable={false} sortInfo={pageInfo.colSortDirs}>
        {renderColumns()}
      </Table>
    );
  };

  /**
   * Update search detail
   */
  updateSearchCriteria = (...args) => {
    let store = this.props.store || {};
    return store.updateSearchCriteria.call(store, ...args);
  };

  /**
   * Handle search
   */
  onSearch = (...args) => {
    let store = this.props.store || {};
    return store.search.call(store, ...args);
  };

  /**
   * Handle change page
   */
  goToPage = (...args) => {
    let store = this.props.store || {};
    return store.goToPage.call(store, ...args);
  };

  /**
   * Handle change page size
   */
  onPageSizeChange = (...args) => {
    let store = this.props.store || {};
    return store.onPageSizeChange.call(store, ...args);
  };

  /**
   * Render component
   */
  render() {
    let props = this.props;
    let { isDashboardMode, pageConstants, localization, onSearch, isPopupMode } = props;
    let store = props.store || {};
    let model = store.model || {};
    let pageInfo = model.pageInfo || {};

    let pageText = pageConstants.PAGE_TEXT;
    let searchOptions = pageConstants.SEARCH_OPTION;
    let currentPageSize = getOr(0, 'length', model.activeInstances);

    let isDataSearchMinSize = this.state.isDataSearchMinSize;
    let widthOnMobile = isDataSearchMinSize ? 1 : 12;
    let isVisible = isDataSearchMinSize || !uiState.isMobile;
    let buttonContainerClass = classnames('u_textRight', {
      'hidden-xs': !isVisible
    });
    return (
      <GridLayout className='subscription-grid'>
        <GridLayout.Row>
          <GridLayout.Col cols='8' xs={widthOnMobile}>
            <DataGridSearch
              options={searchOptions}
              value={model.pageInfo.searchProperty}
              onSearch={onSearch || this.onSearch}
              onSizeStateChange={this.onDataGridSearchSizeChange}
              responsive
              onSearchPropertiesChange={this.updateSearchCriteria}
            />
          </GridLayout.Col>
          {!isPopupMode && (
            <GridLayout.Col cols='4' xs='11' className={buttonContainerClass}>
              <div className='izenda-ActionBarRight'>
                <Button label={pageText.AddText} onClick={this.onClickAdd} />
                {isDashboardMode && (
                  <Button label={localization.getText('COMMON_LABEL_CLOSE')} onClick={this.onClickClose} />
                )}
                <Button
                  className='icon-trash-empty'
                  disabled={!model.hasActiveInstancesSelected}
                  onClick={this.onClickDelete}
                />
              </div>
            </GridLayout.Col>
          )}
        </GridLayout.Row>

        {this.renderTable()}

        <div className='table-footer'>
          <Paginator
            onChange={this.goToPage}
            onDropdownChange={this.onPageSizeChange}
            pageInfo={pageInfo}
            currentPageSize={currentPageSize}
            isDirty={model.checkDirty}
          />
          {isPopupMode && (
            <div className='text-right'>
              <Button label={pageText.AddText} onClick={this.onClickAdd} />
              <Button
                className='icon-trash-empty'
                disabled={!model.hasActiveInstancesSelected}
                onClick={this.onClickDelete}
              />
            </div>
          )}
        </div>
      </GridLayout>
    );
  }

  onDataGridSearchSizeChange = (isMinState: number) => {
    this.setState({
      isDataSearchMinSize: isMinState
    });
  };
}

export default SubscriptionGrid;



// WEBPACK FOOTER //
// ./ui/modules/Subscription/Izenda.SubscriptionGrid.js